/**
 * Typed wrappers for the public auth endpoints on service-identity that the
 * citizen registration form uses. The /register and /login wrappers already
 * live in `customer.ts` (where they were originally co-located with the
 * customer response types) and are left there to keep blast radius small.
 *
 * Endpoints covered here:
 *   - GET  /v1/auth/check-availability?email=&phone=
 *       — used by the register form to pre-warn the operator before they
 *         commit to a verification round-trip.
 *   - POST /v1/auth/email-verification/request
 *       — issues a 6-digit code, sent over the notification channel (email).
 *         In dev (IDENTITY_AUTOVERIFY_REGISTRATION=true) the response body
 *         echoes `devCode` so the form can auto-fill — mirrors the existing
 *         password-reset `devToken` shortcut.
 *   - POST /v1/auth/email-verification/verify
 *       — exchanges (email, code) for confirmation that the address is
 *         deliverable; flips `user_account.email_verified_at` server-side.
 *
 * Phone uniqueness now lives behind the same /register endpoint — a 409
 * response with body `{ code: 'PHONE_ALREADY_REGISTERED' }` is returned when
 * the supplied phone is already on file. Mirrors the EMAIL_ALREADY_REGISTERED
 * shape the existing register wrapper already handles.
 */
import { requestJson, type RequestJsonOptions } from '../api-client';
import { env } from '../env';

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'>;

export interface CheckAvailabilityResponse {
  readonly emailTaken: boolean;
  readonly phoneTaken: boolean;
}

export async function checkAvailability(
  args: { readonly email?: string; readonly phone?: string },
  options: CallOpts = {},
): Promise<CheckAvailabilityResponse> {
  const params = new URLSearchParams();
  if (args.email) params.set('email', args.email);
  if (args.phone) params.set('phone', args.phone);
  const qs = params.toString();
  return requestJson<CheckAvailabilityResponse>({
    method: 'GET',
    baseUrl: env.identityBaseUrl,
    path: `/v1/auth/check-availability${qs.length > 0 ? `?${qs}` : ''}`,
    ...options,
  });
}

export interface RequestEmailVerificationResponse {
  readonly ok: true;
  /** Cleartext 6-digit code, ONLY echoed when
   *  `IDENTITY_AUTOVERIFY_REGISTRATION=true` on the server (dev mode). */
  readonly devCode?: string;
  /** ISO-8601 expiry, present when a token was actually issued. */
  readonly expiresAt?: string;
}

export async function requestEmailVerification(
  email: string,
  options: CallOpts = {},
): Promise<RequestEmailVerificationResponse> {
  return requestJson<RequestEmailVerificationResponse>({
    method: 'POST',
    baseUrl: env.identityBaseUrl,
    path: '/v1/auth/email-verification/request',
    body: { email },
    ...options,
  });
}

export interface VerifyEmailVerificationResponse {
  readonly ok: true;
  readonly verifiedAt: string;
}

export async function verifyEmailVerification(
  email: string,
  code: string,
  options: CallOpts = {},
): Promise<VerifyEmailVerificationResponse> {
  return requestJson<VerifyEmailVerificationResponse>({
    method: 'POST',
    baseUrl: env.identityBaseUrl,
    path: '/v1/auth/email-verification/verify',
    body: { email, code },
    ...options,
  });
}
